/**
 * CenterPointOverlay — a crosshair marker at the location center (the scene
 * origin = mapCenter lng/lat, which scene-feet [0,0] projects to). Imperative
 * positioning on camera move, mirroring PlacementDebugOverlay.
 */
import { useEffect, useRef } from 'react'

interface Props {
  map: mapboxgl.Map | null
  /** scene origin [lng, lat] */
  center: [number, number] | null
  visible: boolean
}

export function CenterPointOverlay({ map, center, visible }: Props) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!map || !center || !visible) return
    const update = () => {
      const el = ref.current
      if (!el) return
      const p = map.project(center)
      el.style.transform = `translate3d(${p.x}px, ${p.y}px, 0) translate(-50%, -50%)`
    }
    let raf = 0
    const schedule = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        update()
      })
    }
    map.on('move', schedule)
    update()
    return () => {
      map.off('move', schedule)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [map, center, visible])

  if (!visible || !center) return null

  return (
    <div className="pointer-events-none absolute inset-0 z-[6] overflow-hidden">
      <div
        ref={ref}
        className="absolute left-0 top-0"
        style={{ transform: 'translate3d(-9999px,-9999px,0)', willChange: 'transform' }}
      >
        <div className="relative flex items-center justify-center">
          <div className="h-5 w-5 rounded-full border-2 border-fuchsia-400/90" />
          <div className="absolute h-7 w-px bg-fuchsia-400/80" />
          <div className="absolute h-px w-7 bg-fuchsia-400/80" />
          <div className="absolute -bottom-5 whitespace-nowrap rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-fuchsia-200">
            Center
          </div>
        </div>
      </div>
    </div>
  )
}
